<template>
  <!-- Context mode: show integrations and references used in this conversation -->
  <div
    v-if="contextItems && contextItems.length > 0"
    class="integration-pills-bar integration-pills-bar--context"
  >
    <span class="context-label">Context</span>
    <div class="integration-pills-icons">
      <div
        v-for="item in contextItems"
        :key="
          item.type === 'integration'
            ? `integration-${integrationPillIdentity(item)}`
            : `reference-${item.kind}-${item.id}`
        "
        class="context-pill"
      >
        <button
          type="button"
          class="context-pill-link"
          @click="openContextItem(item, $event)"
        >
          <img
            v-if="item.type === 'integration' && item.logoUri"
            :src="item.logoUri"
            class="pill-icon"
            :alt="item.name"
          />
          <ReferenceIcon
            v-else-if="item.type === 'reference'"
            :target="{ kind: item.kind, id: item.id }"
            class="context-reference-icon"
          />
          <span class="context-pill-name">{{ item.name }}</span>
        </button>
        <button
          v-if="item.removable"
          type="button"
          class="context-pill-remove"
          :disabled="isContextUpdating"
          :aria-label="`Remove ${item.name} from context`"
          @click="removeContextItem(item)"
        >
          <CloseIcon />
        </button>
      </div>
    </div>
  </div>

  <!-- Default mode: full catalog of active + inactive integrations -->
  <div v-else-if="showBar" class="integration-pills-bar">
    <span class="integration-pills-label">Link objects and integrations</span>
    <div class="integration-pills-icons">
      <button
        type="button"
        class="integration-pill"
        @click="$emit('object-click')"
        @mouseenter="onPillEnter($event, 'Link object')"
        @mouseleave="hideTooltip"
      >
        <ReferenceIcon
          target="object"
          size="l"
          class="pill-icon object-pill-icon"
        />
      </button>
      <template
        v-for="pill in activePills"
        :key="pill.integration.integrationKey"
      >
        <!-- Several connections: pick one from a menu instead of silently
             inserting the first. -->
        <ButtonMenu
          v-if="pill.connections.length > 1"
          :items="connectionItems(pill)"
          :offset="[0, 4]"
          placement="bottom"
          @select="(id) => selectConnection(pill, id)"
        >
          <template #btn>
            <button
              type="button"
              class="integration-pill"
              @click="hideTooltip"
              @mouseenter="onPillEnter($event, activePillTooltip(pill))"
              @mouseleave="hideTooltip"
            >
              <img
                v-if="pill.logoUri"
                :src="pill.logoUri"
                class="pill-icon"
                :alt="pill.name"
              />
              <span class="pill-active-badge" />
            </button>
          </template>
        </ButtonMenu>
        <button
          v-else
          type="button"
          class="integration-pill"
          @click="
            hideTooltip();
            $emit('integration-click', pill);
          "
          @mouseenter="onPillEnter($event, activePillTooltip(pill))"
          @mouseleave="hideTooltip"
        >
          <img
            v-if="pill.logoUri"
            :src="pill.logoUri"
            class="pill-icon"
            :alt="pill.name"
          />
          <span class="pill-active-badge" />
        </button>
      </template>
      <button
        v-for="pill in inactivePills"
        :key="pill.integration.integrationKey"
        type="button"
        class="integration-pill integration-pill--inactive"
        @click="openInactivePill(pill)"
        @mouseenter="onPillEnter($event, `Connect ${pill.name}`)"
        @mouseleave="hideTooltip"
      >
        <img
          v-if="pill.logoUri"
          :src="pill.logoUri"
          class="pill-icon"
          :alt="pill.name"
        />
        <span class="pill-inactive-badge">+</span>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  type PropType,
} from "vue";
import { useRouter } from "vue-router";

import type { OrgId, ReferenceKind } from "@ctrl/controller-rpc";

import ButtonMenu, {
  type ButtonMenuItem,
} from "@/components/elements/ButtonMenu.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import ReferenceIcon from "@/components/icons/ReferenceIcon.vue";
import * as inject from "@/components/inject";
import type { IntegrationCandidate } from "@/components/integrations/candidates";
import { mcpPackageIdFromIntegrationKey } from "@/components/integrations/mcpCandidates";
import { resolveIntegrationOrganizationIds } from "@/components/integrations/useIntegrations";
import { isIntegrationAllowed } from "@/components/settings/integrations/integrationAllowlist";
import { useTooltip, type TooltipContent } from "@/components/tooltip";
import { getOrganizationIdFromWorkspaceId } from "@/components/workspaces";
import { RouteNames } from "@/routes/types";
import { navigateTo, navigateToObject } from "@/utils/navigate";
import { optionalStringProperty } from "@/utils/unknown";

/** A Membrane integration behind a pill. */
export type MembraneIntegration = {
  kind: "membrane";
  integrationKey: string;
  /** Set when the integration is connected. */
  connectionId?: string;
  /** Membrane service id + owning org, for navigating to its settings. */
  appId?: string;
  organizationId?: OrgId;
};

/** An external MCP package/server behind a pill. */
export type McpIntegration = {
  kind: "mcp";
  /**
   * `mcp:`-namespaced integration key (see `mcpCandidates.ts`). For a vetted
   * catalog package it encodes the package id
   * (`mcpPackageIdFromIntegrationKey` recovers it).
   */
  integrationKey: string;
  /**
   * The *enabled* configured server backing the pill. Absent when the
   * package is merely advertised (not installed, or installed but disabled).
   */
  serverId?: string;
};

export type PillIntegration = MembraneIntegration | McpIntegration;

/**
 * One integration pill: display info plus the Membrane or MCP integration it
 * stands for — connected or not (see {@link integrationConnectionId}).
 */
export type IntegrationPill = {
  name: string;
  logoUri?: string;
  removable?: boolean;
  integration: PillIntegration;
};

/**
 * The connected identity behind an integration: the Membrane connection id
 * or the enabled MCP server id. `undefined` when not connected (advertised).
 */
export function integrationConnectionId(
  integration: PillIntegration
): string | undefined {
  return integration.kind === "membrane"
    ? integration.connectionId
    : integration.serverId;
}

/** Stable pill identity, for deduplication and list keys. */
export function integrationPillIdentity(pill: IntegrationPill): string {
  return `${pill.integration.integrationKey}:${
    integrationConnectionId(pill.integration) ?? ""
  }`;
}

export type ReferenceInfo = {
  id: string;
  name: string;
  kind: ReferenceKind;
  removable?: boolean;
};

export type ContextPillItem =
  | ({ type: "integration" } & IntegrationPill)
  | ({ type: "reference" } & ReferenceInfo);

/**
 * An active pill with every connection behind its integration key (as the
 * store's candidates): a single connection inserts directly on click,
 * several offer a picker menu.
 */
type ActivePill = IntegrationPill & { connections: IntegrationCandidate[] };

/** The integration a pill emits when bound to this candidate. */
function candidateIntegration(
  candidate: IntegrationCandidate
): PillIntegration {
  return candidate.kind === "membrane"
    ? {
        kind: "membrane",
        integrationKey: candidate.integrationKey,
        connectionId: candidate.connectionId,
        appId: candidate.appId,
        organizationId: candidate.organizationId,
      }
    : {
        kind: "mcp",
        integrationKey: candidate.integrationKey,
        serverId: candidate.connectionId,
      };
}

export default defineComponent({
  name: "IntegrationPillsBar",
  components: { ButtonMenu, CloseIcon, ReferenceIcon },
  props: {
    workspaceId: {
      type: String,
      required: true,
    },
    contextItems: {
      type: Array as PropType<ContextPillItem[]>,
      default: null,
    },
    isContextUpdating: { type: Boolean, default: false },
    /**
     * Organizations whose connections back the active pills. Authoritative
     * when set (the assistant passes every organization in the profile);
     * `undefined` falls back to the workspace's own organization.
     */
    organizationIds: {
      type: Array as PropType<OrgId[] | undefined>,
      default: undefined,
    },
  },
  emits: {
    "object-click": () => true,
    "integration-click": (_pill: IntegrationPill) => true,
    "open-context-integration": (_pill: IntegrationPill) => true,
    "remove-context-integration": (_pill: IntegrationPill) => true,
    "remove-context-reference": (_reference: ReferenceInfo) => true,
  },
  setup(props, { emit }) {
    const controller = inject.controller();
    const profile = inject.profile();
    const router = useRouter();

    // One shared tooltip, moved to the hovered pill (`useTooltip` recreates
    // its instance when the target changes).
    const tooltipEl = ref<HTMLElement | undefined>(undefined);
    const tooltipData = ref<TooltipContent>(undefined);
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(tooltipEl, tooltipData, "top");
    onBeforeUnmount(releaseTooltip);

    const onPillEnter = (event: MouseEvent, content: TooltipContent) => {
      if (!(event.currentTarget instanceof HTMLElement)) return;
      tooltipEl.value = event.currentTarget;
      tooltipData.value = content;
      showTooltip();
    };

    const organizationId = computed<OrgId | undefined>(() =>
      props.workspaceId
        ? getOrganizationIdFromWorkspaceId(profile.value, props.workspaceId)
        : undefined
    );

    const integrationsStore = inject.integrations();
    const integrations = integrationsStore.forOrganization(organizationId);

    // Active pills come from the same multi-organization candidates view as
    // the editors' `@` menu, so the assistant (which passes every
    // organization) sees all connections, not just the current workspace's.
    const candidateOrganizationIds = computed(() =>
      resolveIntegrationOrganizationIds(
        props.organizationIds,
        organizationId.value
      )
    );
    const candidateData = integrationsStore.candidatesFor(
      candidateOrganizationIds
    );

    const activePills = computed<ActivePill[]>(() => {
      const byKey = new Map<string, ActivePill>();
      for (const candidate of candidateData.candidates.value) {
        if (!candidate.logoUri) continue;
        const key = candidate.integrationKey;
        const existing = byKey.get(key);
        if (existing) {
          existing.connections.push(candidate);
          continue;
        }
        byKey.set(key, {
          // The pill stands for the integration, not one connection: title
          // it after the integration whenever the catalog knows it.
          name: candidateData.lookup.getMetaByKey(key)?.name ?? candidate.name,
          logoUri: candidate.logoUri,
          integration: candidateIntegration(candidate),
          connections: [candidate],
        });
      }
      return [...byKey.values()];
    });

    const inactivePills = computed<IntegrationPill[]>(() => {
      const connectedKeys = new Set(
        activePills.value.map((p) => p.integration.integrationKey)
      );
      const pills: IntegrationPill[] = [];
      for (const s of integrations.services.value) {
        const key = optionalStringProperty(s, "key") ?? "";
        const logoUri = optionalStringProperty(s, "logoUri") ?? "";
        if (
          !key ||
          !isIntegrationAllowed(key) ||
          !logoUri ||
          connectedKeys.has(key)
        )
          continue;
        pills.push({
          name: String(s.name ?? key),
          logoUri,
          integration: { kind: "membrane", integrationKey: key },
        });
      }
      return pills;
    });

    const showBar = computed(
      () => activePills.value.length > 0 || inactivePills.value.length > 0
    );

    // The integration's name, with the connection as detail when the pill is
    // backed by a single connection named differently (e.g. an account).
    const activePillTooltip = (pill: ActivePill): TooltipContent => {
      const connection =
        pill.connections.length === 1 ? pill.connections[0] : undefined;
      return connection && connection.name !== pill.name
        ? { text: pill.name, detail: connection.name }
        : pill.name;
    };

    const connectionItems = (pill: ActivePill): ButtonMenuItem[] => {
      // Surface the scope only when the connections span several of them.
      const scopes = new Set(
        pill.connections.map((connection) => connection.organizationName ?? "")
      );
      return pill.connections.map((connection) => ({
        id: connection.connectionId,
        text: connection.name,
        detail: scopes.size > 1 ? connection.organizationName : undefined,
      }));
    };

    // Emits the pill re-bound to the chosen connection.
    const selectConnection = (pill: ActivePill, connectionId: string) => {
      const connection = pill.connections.find(
        (candidate) => candidate.connectionId === connectionId
      );
      if (!connection) return;
      emit("integration-click", {
        name: connection.name,
        logoUri: pill.logoUri,
        integration: candidateIntegration(connection),
      });
    };

    const navigateToIntegrations = () => {
      const orgId = organizationId.value;
      if (!orgId) return;
      void navigateTo(RouteNames.SettingsIntegrations, router, {
        params: { orgId },
      });
    };

    // Open the integration's own settings screen (MCP package page or
    // Membrane integration page); the generic catalog is only a fallback.
    const openInactivePill = (pill: IntegrationPill) => {
      hideTooltip();
      const orgId = organizationId.value;
      if (!orgId) return;
      const { integration } = pill;
      if (integration.kind === "mcp") {
        const packageId = mcpPackageIdFromIntegrationKey(
          integration.integrationKey
        );
        if (!packageId) {
          navigateToIntegrations();
          return;
        }
        void navigateTo(RouteNames.SettingsMcpIntegration, router, {
          params: { orgId, packageId },
        });
        return;
      }
      // An inactive Membrane pill's integration key is the service key.
      void navigateTo(RouteNames.SettingsMembraneIntegration, router, {
        params: { orgId, appKey: integration.integrationKey },
      });
    };

    const onReferenceClick = (reference: ReferenceInfo, event: MouseEvent) => {
      void navigateToObject(
        router,
        controller,
        { kind: reference.kind, id: reference.id },
        event
      );
    };

    const openContextItem = (item: ContextPillItem, event: MouseEvent) => {
      if (item.type === "integration") {
        emit("open-context-integration", item);
      } else {
        onReferenceClick(item, event);
      }
    };

    const removeContextItem = (item: ContextPillItem) => {
      if (item.type === "integration") {
        emit("remove-context-integration", item);
      } else {
        emit("remove-context-reference", item);
      }
    };

    return {
      activePills,
      inactivePills,
      showBar,
      connectionItems,
      selectConnection,
      activePillTooltip,
      onPillEnter,
      hideTooltip,
      openInactivePill,
      onReferenceClick,
      openContextItem,
      removeContextItem,
      integrationPillIdentity,
    };
  },
});
</script>

<style scoped lang="scss">
.integration-pills-bar {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 16px 12px 10px 12px;
  margin: -10px 1px 0 1px;
  overflow: hidden;
  border-radius: $border-radius-m;
  @include themed() {
    background-color: t($grey-03);
    border: 1px solid t($grey-10);
  }
}

.integration-pills-bar--context {
  justify-content: flex-start;
}

.context-label {
  flex-shrink: 0;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-30);
  }
}

.context-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding-block: 3px;
  padding-inline-end: 8px;
  padding-inline-start: 6px;
  border-radius: 999px;
  flex-shrink: 0;
  border: none;
  cursor: default;
  @extend %font-caption-regular;
  @include themed() {
    background-color: t($grey-10);
    color: t($primary);
  }

  &:hover {
    @include themed() {
      background-color: t($grey-15);
    }
  }
}

.context-pill-link,
.context-pill-remove {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
}

.context-pill-link {
  gap: 5px;
  color: inherit;
  cursor: default;
}

.context-pill-remove {
  width: 12px;
  height: 12px;
  justify-content: center;
  border-radius: $border-radius-s;
  cursor: default;

  svg {
    width: 10px;
    height: 10px;
  }

  :deep(.fill) {
    @include themed() {
      fill: t($grey-40);
    }
  }

  &:hover:not(:disabled) {
    @include themed() {
      background-color: t($grey-20);
    }

    :deep(.fill) {
      @include themed() {
        fill: t($primary);
      }
    }
  }
}

.context-pill-name {
  white-space: nowrap;
}

.context-reference-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;

  :deep(.fill) {
    @include themed() {
      fill: t($grey-50);
    }
  }
}

.context-object-emoji {
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
}

.integration-pills-label {
  flex-shrink: 0;
  @extend %font-caption-regular;
  @include themed() {
    color: t($grey-30);
  }
}

.integration-pills-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.integration-pill {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 2px;
  border: none;
  background: transparent;
  border-radius: $border-radius-s;
  cursor: default;
  opacity: 0.85;
  @include transition(opacity 0.1s ease-in, transform 0.1s ease-in);

  &:hover {
    opacity: 1;
    transform: scale(1.05);
  }

  &--inactive {
    opacity: 0.2;

    &:hover {
      opacity: 1;
    }
  }
}

.pill-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  object-fit: contain;
  border-radius: $border-radius-s;
}

.object-pill-icon {
  width: 20px;
  height: 20px;
}

.pill-active-badge {
  position: absolute;
  bottom: 0;
  inset-inline-end: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: $green;
  border: 1.5px solid;
  @include themed() {
    border-color: t($bg);
  }
}

.pill-inactive-badge {
  position: absolute;
  bottom: -1px;
  inset-inline-end: -1px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  line-height: 1;
  font-weight: 700;
  border: 1px solid;
  @include themed() {
    background-color: t($grey-15);
    color: t($grey-60);
    border-color: t($bg);
  }
}
</style>
